// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Shunt — every screen's markup except the yard itself. Pure string builders, so the copy is in
// one place and the tests can read it.

import { escapeHtml, fmtTime, moveLabel, parLabel } from './dom';
import { letterOf, liveryOf } from './palette';
import { MODE_IDS, modeOf, type Mode, type ModeId } from './modes';
import type { PeerResult } from './race';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

/** The order slip: the train the yard is asking for, front of the train first. */
export function slipHtml(target: readonly number[], seated: number): string {
  const cars = target
    .map((id, i) => {
      const done = i < seated;
      return `<span class="slip-car${done ? ' done' : ''}" style="--livery:${liveryOf(id)}">${letterOf(id)}</span>`;
    })
    .join('<span class="slip-link" aria-hidden="true"></span>');
  return `<div class="slip" aria-label="Order slip: make up ${target.map((t) => letterOf(t)).join(', ')}">
      <span class="slip-title">Order slip</span>
      <span class="slip-loco" aria-hidden="true"></span>${cars}
    </div>`;
}

export function menuHtml(o: { modeId: ModeId; best: number | null; muted: boolean; dateStr: string }): string {
  const mode = modeOf(o.modeId);
  return `
    <section class="screen menu">
      <header class="brand">
        <h1 class="title">Shunt</h1>
        <p class="tagline">Push, pull and run round to make up the train in the fewest moves.</p>
      </header>

      <div class="modes" role="group" aria-label="Choose a yard">
        ${MODE_IDS.map(
          (id) =>
            `<button class="chip${id === o.modeId ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === o.modeId}">${escapeHtml(
              modeOf(id).name,
            )}</button>`,
        ).join('')}
      </div>
      <p class="mode-blurb">${escapeHtml(mode.blurb)}</p>

      <div class="menu-actions">
        <button class="btn primary" id="playDaily">Today's yard</button>
        <button class="btn" id="playRandom">Random yard</button>
        <button class="btn" id="playFriends">Play with friends</button>
      </div>

      <p class="best">${o.best === null ? 'No solve yet on this yard.' : `Best here: <strong>${moveLabel(o.best)}</strong>`} · <span class="muted">${escapeHtml(
        o.dateStr,
      )}</span></p>

      <div class="menu-links">
        <button class="linkbtn" id="help">How to play</button>
        <button class="linkbtn" id="about">About</button>
        <button class="linkbtn" id="mute">${o.muted ? 'Sound off' : 'Sound on'}</button>
      </div>
    </section>`;
}

export function helpHtml(): string {
  return `
    <h2>How to play</h2>
    <p>A yard of wagons, and an <strong>order slip</strong> saying which ones to make up into a train
       and <strong>in what order</strong>. Get them onto the departure road, front of the train first.</p>
    <ol class="help-steps">
      <li><strong>Tap a road</strong> and the loco propels whatever it is coupled to straight in.</li>
      <li><strong>Tap a wagon</strong> to draw it — and everything between it and the loco — back out
          onto the headshunt. Tap the loco to come out light, with nothing.</li>
      <li>Every trip in and every trip out is <strong>one move</strong>, because each one is a change
          of direction. That is the expensive thing in a real yard, so the game is about planning
          trips, not about how many wagons you drag at once.</li>
    </ol>
    <p>The loco only ever works the near end of a road, so <strong>the order of a rake never
       reverses</strong> — a wagon you want behind another has to go the long way round, through a
       third road. That is the whole puzzle.</p>
    <p><strong>Par</strong> is the shortest possible solve, worked out exactly rather than guessed,
       so "on par" really is perfect. <strong>Set back</strong> undoes a move and gives it back to
       you — free, as often as you like, because a mis-tap should never cost you a yard.</p>
    <button class="btn sheet-close">Right away</button>`;
}

export function aboutHtml(): string {
  return `
    <h2>About Shunt</h2>
    <p>A shunting puzzle: one locomotive, a handful of dead-end roads, and a train to make up in the
       fewest moves. The manoeuvre is an old one that yard crews and model railwaymen have set each
       other for a century; the yards, the wagons and the code here are original.</p>
    <p>Every yard is generated by searching outward from a finished train, so it is guaranteed
       solvable and its <strong>par is the proven optimum</strong> — not an estimate. Today's yard is
       the same for everyone in the world and changes at midnight UTC.</p>
    <p><strong>Playing with friends</strong> is peer-to-peer: browsers talk straight to each other
       over WebRTC, and a public signalling relay is used only to introduce them. There is no game
       server and no game state is stored anywhere. Nothing about you is recorded beyond anonymous,
       cookie-less page-view counts.</p>
    <p class="muted">${ATTRIB}</p>
    <button class="btn sheet-close">Close</button>`;
}

export function soloResultsHtml(o: {
  mode: Mode;
  won: boolean;
  moves: number;
  par: number;
  undos: number;
  ms: number;
  best: number | null;
  daily: boolean;
  isNewBest: boolean;
}): string {
  const over = o.moves - o.par;
  const head = !o.won
    ? 'Yard left as you found it'
    : over <= 0
      ? 'A perfect shunt.'
      : over <= 2
        ? 'Train made up.'
        : 'Train made up.';
  const clean = o.won && o.undos === 0 ? '<span class="badge clean">Clean — no set-backs</span>' : '';
  return `
    <section class="screen results">
      <h2 class="results-head">${head}</h2>
      ${
        o.won
          ? `<div class="score-row">
               <div class="score"><b>${o.moves}</b><span>moves</span></div>
               <div class="score"><b>${o.par}</b><span>par</span></div>
               <div class="score ${over <= 0 ? 'good' : ''}"><b>${parLabel(o.moves, o.par)}</b><span>result</span></div>
             </div>
             <p class="results-note">${fmtTime(o.ms)} on the clock${o.undos ? ` · ${o.undos} set-back${o.undos === 1 ? '' : 's'}` : ''}</p>
             ${clean}
             ${o.isNewBest ? '<p class="results-note good">Your best on this yard yet.</p>' : ''}`
          : `<p class="results-note">You stopped with the train unmade. Par on this yard was <strong>${o.par}</strong> — tap <em>Watch the best line</em> to see it done.</p>`
      }
      ${o.best !== null ? `<p class="results-note muted">Best on ${escapeHtml(o.mode.name)}: ${moveLabel(o.best)}</p>` : ''}
    </section>`;
}

/**
 * Everyone's round, not just yours (principle #9): every player's move count against the SAME
 * proven par, including the ones who did not finish, so the table says exactly how far off the
 * best possible line each person was.
 */
export function multiResultsHtml(rows: PeerResult[], selfSeat: number | null, mode: Mode, par: number, tally: number[]): string {
  const body = rows
    .map((r) => {
      const me = r.seat === selfSeat;
      const outcome = r.left
        ? '<span class="muted">left</span>'
        : !r.done
          ? `<span class="muted">shunting… ${r.seated} up</span>`
          : r.won
            ? `<b>${r.moves}</b> <span class="muted">${parLabel(r.moves, par)}</span>`
            : `<span class="muted">unmade · ${r.seated} up</span>`;
      const wins = tally[r.seat] ? `<span class="tally" title="rounds won">${tally[r.seat]}</span>` : '';
      const clean = r.done && r.won && r.undos === 0 ? '<span class="badge clean small">clean</span>' : '';
      return `<li class="rrow${me ? ' me' : ''}${r.done && r.won ? ' won' : ''}">
          <span class="rname">${escapeHtml(r.name)}${me ? ' <span class="muted">(you)</span>' : ''} ${clean}</span>
          <span class="rscore">${outcome}${r.done && r.won ? `<span class="muted rtime">${fmtTime(r.ms)}</span>` : ''}</span>
          ${wins}
        </li>`;
    })
    .join('');
  return `
    <h2 class="results-head">${escapeHtml(mode.name)} — par ${par}</h2>
    <ol class="rlist">${body}</ol>
    <p class="results-note muted">Everyone shunted the identical yard. Par is the shortest solve that
       exists for it, so <em>on par</em> is as good as anyone could have done.</p>`;
}

/** The replay panel's caption. */
export function replayHtml(par: number): string {
  return `<div class="replay-cap"><strong>The best line</strong> — ${moveLabel(par)}, the shortest this yard can be shunted.</div>`;
}
